/**
 * Subagent Streaming Handlers — SUBAGENT_START/TEXT_DELTA/TOOL_USE/TOOL_RESULT/END
 *
 * Handles real-time streaming events from subagents spawned via the Task tool.
 * Groups content by toolUseId on the parent ToolUseBlock.
 */

import type { Socket } from "socket.io-client";
import { EVENTS } from "@/constants/events";
import { isTerminalLaneStatus, laneStatusForStopReason } from "@bash/shared";
import { useTaskStore } from "../taskStore";
import { useAgentStore } from "../agentStore";
import type { ToolUseBlock, ContentBlock } from "../types";
import type { TextBlock } from "@/types";
import type { ClientPayloadOf } from "../socketService";

/** The shape the block walk needs. Structural on purpose — the store hands the
 *  handlers a loosely-typed task. */
type BlockLike = {
  type: string;
  toolUseId?: string;
  subagentContent?: BlockLike[];
};
type TaskLike = {
  streamingMessage: { content: BlockLike[] } | null;
  messages: Array<{ role: string; content: BlockLike[] }>;
};

/** Recursion cap for the nest walk. Real nesting is one to three levels; the cap
 *  exists only so a malformed (self-referential) stream cannot blow the stack. A
 *  miss at the cap surfaces through the same unroutable warning as any other miss. */
const MAX_NEST_DEPTH = 16;

/** Depth-first search for a tool_use block, descending into `subagentContent`.
 *
 *  A subagent's own tool_use blocks live inside its parent block's
 *  `subagentContent` (pushed by handleSubagentToolUse below) — never at the top
 *  level. So a subagent that spawns a subagent has its lane block one level down,
 *  that child's another level down, and so on. Walking the nest is what makes the
 *  lookup total: depth N routes by construction, with no per-depth code. */
function searchBlocks(
  blocks: BlockLike[] | undefined,
  toolUseId: string,
  depth: number
): ToolUseBlock | null {
  if (!blocks || depth > MAX_NEST_DEPTH) return null;
  for (const block of blocks) {
    if (block.type !== "tool_use") continue;
    if (block.toolUseId === toolUseId) return block as unknown as ToolUseBlock;
    const nested = searchBlocks(block.subagentContent, toolUseId, depth + 1);
    if (nested) return nested;
  }
  return null;
}

/** Find a ToolUseBlock by toolUseId across streaming and committed messages, at
 *  any subagent nesting depth. Pure over the task shape — no index, no cache: an
 *  identity index would go stale the moment immer re-parents a block. */
function findToolUseBlock(
  task: TaskLike,
  toolUseId: string
): ToolUseBlock | null {
  // Check streaming message first (most common case)
  const streaming = searchBlocks(task.streamingMessage?.content, toolUseId, 0);
  if (streaming) return streaming;

  // Fallback: check committed messages (multi-turn race)
  for (const msg of task.messages) {
    if (msg.role !== "assistant") continue;
    const committed = searchBlocks(msg.content, toolUseId, 0);
    if (committed) return committed;
  }

  return null;
}

/** One warning per (taskId, toolUseId) lane — a broken lane must leave a trace,
 *  but a text-delta stream must not flood the console. Bounded: a long-lived tab
 *  that accumulates many dead lanes resets the set rather than growing forever. */
const warnedUnroutable = new Set<string>();
const WARNED_UNROUTABLE_CAP = 200;

/** Resolve the lane block for an event, or leave a trace and return null.
 *
 *  Every handler used to do a bare `if (!block) return;`, so an event that failed
 *  to route vanished with nothing to grep — which is how nested lanes rendered
 *  empty for days while the work was genuinely running. */
function resolveLaneBlock(
  task: TaskLike,
  taskId: string,
  toolUseId: string,
  event: string
): ToolUseBlock | null {
  const block = findToolUseBlock(task, toolUseId);
  if (block) return block;

  const key = `${taskId}:${toolUseId}`;
  if (!warnedUnroutable.has(key)) {
    if (warnedUnroutable.size >= WARNED_UNROUTABLE_CAP)
      warnedUnroutable.clear();
    warnedUnroutable.add(key);
    console.warn(
      `[Socket] ${event}: no tool_use block for toolUseId=${toolUseId} (task=${taskId.slice(
        0,
        8
      )}) — event dropped`
    );
  }
  return null;
}

/**
 * Per-frame delta batching, mirroring the main text lane's `eventQueue` /
 * `scheduleFlush` in streamingHandlers.ts.
 *
 * Each delta used to be its own immer mutation. That was affordable while nested
 * lanes were silent; this fix makes depth-2 lanes emit every delta they were
 * previously swallowing, so the SAME handler now takes more events AND pays more
 * per event (a deeper tree costs more to produce structurally). Batching a frame's
 * worth into one mutation is what keeps a long nested research run from janking.
 *
 * ORDERING is the constraint that shapes this. `subagentContent` is a
 * CHRONOLOGICAL stream, so a buffered delta must never land after a tool_use that
 * arrived later — that would reorder the rail. The house answer already exists:
 * `drainTextForTask` in streamingHandlers.ts drains synchronously before a tool
 * block is added, for exactly this reason. Every subagent handler that touches the
 * stream calls `flushSubagentDeltas()` first, so batching can only ever coalesce
 * ADJACENT deltas and arrival order is preserved by construction.
 */
type PendingDelta = { taskId: string; toolUseId: string; delta: string };
const deltaQueue: PendingDelta[] = [];
let rafHandle: number | null = null;
// Tracked separately from the handle, deliberately. If requestAnimationFrame runs
// its callback SYNCHRONOUSLY, the callback clears state and only THEN does the
// assignment below write the handle — so a handle-only guard latches on forever
// and every later delta is buffered but never scheduled.
let flushScheduled = false;

/** Apply one buffered delta. The lane is re-resolved at flush time, not at arrival
 *  time: a block can be created by an event that landed in between. */
function applyPendingDelta(tasks: Map<string, unknown>, p: PendingDelta): void {
  const task = tasks.get(p.taskId);
  if (!task) return;

  const block = resolveLaneBlock(
    task as TaskLike,
    p.taskId,
    p.toolUseId,
    EVENTS.SUBAGENT_TEXT_DELTA
  );
  if (!block) return;

  // Keep flat accumulated text for legacy summary/preview consumers
  block.subagentText = (block.subagentText || "") + p.delta;

  // Build chronological content stream: append delta to the trailing text
  // block, or open a new text block if the last entry isn't text (i.e.
  // the subagent just ran a tool and is now resuming narration).
  if (!block.subagentContent) block.subagentContent = [];
  const last = block.subagentContent[block.subagentContent.length - 1];
  if (last && last.type === "text") {
    (last as TextBlock).text = ((last as TextBlock).text || "") + p.delta;
  } else {
    const newText: TextBlock = { type: "text", text: p.delta };
    block.subagentContent.push(newText as ContentBlock);
  }
}

/** Drain every buffered delta into the store NOW. Called by the rAF flush and, as
 *  the ordering barrier, by each handler that appends to the same stream. */
function flushSubagentDeltas(): void {
  if (deltaQueue.length === 0) return;
  const batch = deltaQueue.splice(0);
  useTaskStore.getState()._mutateTasks((tasks) => {
    for (const p of batch) applyPendingDelta(tasks as Map<string, unknown>, p);
  });
}

function scheduleSubagentFlush(): void {
  if (flushScheduled) return;
  flushScheduled = true;
  rafHandle = requestAnimationFrame(() => {
    flushScheduled = false;
    rafHandle = null;
    flushSubagentDeltas();
  });
}

export function registerSubagentHandlers(socket: Socket): () => void {
  const taskStore = useTaskStore;

  // UNDECLARED by the wire contract (s2-07e finding): sessionId — read here (the taskId fallback), absent from SubagentStartPayloadSchema; the relay stamps taskId on every subagent:* frame.
  const handleSubagentStart = (
    data: ClientPayloadOf<typeof EVENTS.SUBAGENT_START> & { sessionId?: string }
  ) => {
    const taskId = data.taskId || data.sessionId;
    if (!taskId) return;

    // Ordering barrier: this event appends to (or seals) the same chronological
    // stream the buffered deltas write to, so any pending delta must land first.
    flushSubagentDeltas();

    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(taskId);
      if (!task) return;

      const block = resolveLaneBlock(
        task as any,
        taskId,
        data.toolUseId,
        EVENTS.SUBAGENT_START
      );
      if (!block) return;

      // Identity metadata is always safe to (re)apply.
      block.subagentType = data.agentSlug;
      block.subagentName = data.agentName || data.agentSlug;
      block.subagentIcon = data.agentIcon;
      block.subagentColor = data.agentColor;

      // Monotonic status. A start can arrive AFTER the lane already sealed —
      // subagent:end delivered out of order, or the parent tool resolved first
      // (#1367 family). Never regress a terminal lane back to 'running', and
      // never wipe an already-accumulated stream on a late/duplicate start.
      // A stopped / capped / refused lane is terminal too: a late start must not
      // reopen it as a running one with a live spinner and a lost reason.
      if (isTerminalLaneStatus(block.subagentStatus)) return;

      block.subagentStatus = "running";
      block.subagentText = block.subagentText ?? "";
      if (!block.subagentContent) block.subagentContent = [];
    });

    // Refresh agent store if we don't know this agent yet
    const agents = useAgentStore.getState().agents;
    if (!agents.find((a) => a.slug === data.agentSlug)) {
      useAgentStore.getState().fetchMyAgents();
    }
  };

  // UNDECLARED by the wire contract (s2-07e finding): sessionId — read here (the taskId fallback), absent from SubagentTextDeltaPayloadSchema.
  const handleSubagentTextDelta = (
    data: ClientPayloadOf<typeof EVENTS.SUBAGENT_TEXT_DELTA> & {
      sessionId?: string;
    }
  ) => {
    const taskId = data.taskId || data.sessionId;
    if (!taskId) return;

    // Buffer — flushed once per animation frame, or sooner by the ordering
    // barrier below. Coalesce only with the IMMEDIATELY preceding entry and only
    // when it is the same lane: merging across lanes, or across an intervening
    // event, is what would reorder the rail.
    const last = deltaQueue[deltaQueue.length - 1];
    if (last && last.taskId === taskId && last.toolUseId === data.toolUseId) {
      last.delta += data.delta;
    } else {
      deltaQueue.push({ taskId, toolUseId: data.toolUseId, delta: data.delta });
    }
    scheduleSubagentFlush();
  };

  // UNDECLARED by the wire contract (s2-07e finding): sessionId — read here (the taskId fallback), absent from SubagentToolUsePayloadSchema.
  const handleSubagentToolUse = (
    data: ClientPayloadOf<typeof EVENTS.SUBAGENT_TOOL_USE> & {
      sessionId?: string;
    }
  ) => {
    const taskId = data.taskId || data.sessionId;
    if (!taskId) return;

    // Ordering barrier: this event appends to (or seals) the same chronological
    // stream the buffered deltas write to, so any pending delta must land first.
    flushSubagentDeltas();

    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(taskId);
      if (!task) return;

      const block = resolveLaneBlock(
        task as any,
        taskId,
        data.toolUseId,
        EVENTS.SUBAGENT_TOOL_USE
      );
      if (!block) return;

      // Legacy flat list — keep populated for backward compatibility
      if (!block.subagentTools) block.subagentTools = [];
      const now = Date.now();
      block.subagentTools.push({
        tool: data.toolName,
        input: data.toolInput,
        subToolUseId: data.subToolUseId,
        status: "running",
        startedAt: now,
      });

      // Chronological stream — push a real ToolUseBlock so the same
      // ContentBlockRenderer used for the main agent renders it inline.
      if (!block.subagentContent) block.subagentContent = [];
      const subTool: ToolUseBlock = {
        type: "tool_use",
        toolUseId:
          data.subToolUseId ||
          `${data.toolUseId}-${block.subagentContent.length}`,
        toolName: data.toolName,
        toolInput: data.toolInput,
        status: "running",
        startTime: new Date(now),
      };
      block.subagentContent.push(subTool as ContentBlock);
    });
  };

  // UNDECLARED by the wire contract (s2-07e finding): sessionId — read here (the taskId fallback), absent from SubagentToolResultPayloadSchema.
  const handleSubagentToolResult = (
    data: ClientPayloadOf<typeof EVENTS.SUBAGENT_TOOL_RESULT> & {
      sessionId?: string;
    }
  ) => {
    const taskId = data.taskId || data.sessionId;
    if (!taskId) return;

    // Ordering barrier: this event appends to (or seals) the same chronological
    // stream the buffered deltas write to, so any pending delta must land first.
    flushSubagentDeltas();

    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(taskId);
      if (!task) return;

      const block = resolveLaneBlock(
        task as any,
        taskId,
        data.toolUseId,
        EVENTS.SUBAGENT_TOOL_RESULT
      );
      if (!block) return;

      const now = Date.now();

      // Legacy flat list update
      if (block.subagentTools) {
        for (let i = block.subagentTools.length - 1; i >= 0; i--) {
          const entry = block.subagentTools[i];
          if (entry.status !== "running") continue;
          if (
            data.subToolUseId &&
            entry.subToolUseId &&
            entry.subToolUseId !== data.subToolUseId
          )
            continue;
          entry.status = "completed";
          entry.result = data.result;
          entry.completedAt = now;
          break;
        }
      }

      // Chronological stream update — find the running tool_use and complete it.
      // The renderer keys off ToolUseBlock.status === 'completed' + result to
      // flip its UI from in-flight to done.
      if (block.subagentContent) {
        for (let i = block.subagentContent.length - 1; i >= 0; i--) {
          const entry = block.subagentContent[i];
          if (entry.type !== "tool_use") continue;
          const t = entry as ToolUseBlock;
          if (t.status !== "running") continue;
          if (
            data.subToolUseId &&
            t.toolUseId &&
            t.toolUseId !== data.subToolUseId
          )
            continue;
          t.status = "completed";
          t.result = data.result;
          t.endTime = new Date(now);
          break;
        }
      }
    });
  };

  // UNDECLARED by the wire contract (s2-07e finding): sessionId — read here (the taskId fallback), absent from SubagentEndPayloadSchema.
  // The inline type also declared usage / agentOutputStatus: no emitter sends them and nothing here read them.
  const handleSubagentEnd = (
    data: ClientPayloadOf<typeof EVENTS.SUBAGENT_END> & { sessionId?: string }
  ) => {
    const taskId = data.taskId || data.sessionId;
    if (!taskId) return;

    // Ordering barrier: this event appends to (or seals) the same chronological
    // stream the buffered deltas write to, so any pending delta must land first.
    flushSubagentDeltas();

    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(taskId);
      if (!task) return;

      const block = resolveLaneBlock(
        task as any,
        taskId,
        data.toolUseId,
        EVENTS.SUBAGENT_END
      );
      if (!block) return;

      // Orchestration walls: a stopped / capped / failed helper never reads
      // "completed" in the live store either. Absent reason = the pre-walls
      // contract, byte-identical to what this handler always did.
      //
      // MONOTONIC, for the same reason the durable twin is: the reconnect resync
      // re-emits an END for every settled lane (a stopped one included) with
      // `result` + `resync` and no stopReason. Overwriting on a reason-less END
      // would let one relay flap turn "stopped, partial" into a green tick.
      // The downgrade guard covers `error` as well as `stopped`: a failed lane is
      // settled too, and the resync's synthetic END carries no reason.
      const reason = data.stopReason;
      const settled =
        block.subagentStatus === "stopped" || block.subagentStatus === "error";
      if (!(reason === undefined && settled)) {
        block.subagentStatus = laneStatusForStopReason(reason);
        if (reason !== undefined) block.subagentStopReason = reason;
      }
      if (data.result) block.subagentOutput = data.result;
      // Use task_notification summary as the primary display text (readable AI summary)
      if (data.summary) block.subagentSummary = data.summary;

      // Store structured AgentOutput data directly on the block
      // so the UI doesn't need to regex-parse <usage> tags
      if (
        data.totalDurationMs != null ||
        data.totalToolUseCount != null ||
        data.totalTokens != null
      ) {
        block.subagentProgress = {
          description:
            data.summary || block.subagentProgress?.description || "",
          totalTokens:
            data.totalTokens ?? block.subagentProgress?.totalTokens ?? 0,
          toolUses:
            data.totalToolUseCount ?? block.subagentProgress?.toolUses ?? 0,
          durationMs:
            data.totalDurationMs ?? block.subagentProgress?.durationMs ?? 0,
          lastToolName: block.subagentProgress?.lastToolName,
        };
      }
    });
  };

  socket.on(EVENTS.SUBAGENT_START, handleSubagentStart);
  socket.on(EVENTS.SUBAGENT_TEXT_DELTA, handleSubagentTextDelta);
  socket.on(EVENTS.SUBAGENT_TOOL_USE, handleSubagentToolUse);
  socket.on(EVENTS.SUBAGENT_TOOL_RESULT, handleSubagentToolResult);
  socket.on(EVENTS.SUBAGENT_END, handleSubagentEnd);

  return () => {
    // Cancel the pending frame, but DRAIN rather than discard — a teardown that
    // threw away a buffered delta would lose the tail of a lane's narration,
    // which is the exact failure this whole change is about.
    if (flushScheduled && rafHandle !== null) cancelAnimationFrame(rafHandle);
    flushScheduled = false;
    rafHandle = null;
    flushSubagentDeltas();

    socket.off(EVENTS.SUBAGENT_START, handleSubagentStart);
    socket.off(EVENTS.SUBAGENT_TEXT_DELTA, handleSubagentTextDelta);
    socket.off(EVENTS.SUBAGENT_TOOL_USE, handleSubagentToolUse);
    socket.off(EVENTS.SUBAGENT_TOOL_RESULT, handleSubagentToolResult);
    socket.off(EVENTS.SUBAGENT_END, handleSubagentEnd);
  };
}
